import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import logger from '../../utils/logger';

/**
 * Services of a job: the main service (first row) plus add-on lines (inside
 * the oven, driveway power wash…), each with its own price and optionally done
 * by one of the job's technicians. The job total is the main price plus the
 * add-ons.
 *
 * `main` (optional): { label, price, technicianId, onTechnicianChange,
 * onPriceChange, priceDisabled, priceHelperText } renders the main service as
 * the first row. Without onPriceChange its price is shown read-only (the form
 * has its own Price field).
 *
 * A line in this editor: { key, description, quantity, unitPrice (dollars),
 * serviceCatalogId, technicianId }.
 */

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
let nextKey = 0;
const newKey = () => `line-${Date.now()}-${nextKey++}`;

export const emptyExtraLine = () => ({
  key: newKey(),
  description: '',
  quantity: 1,
  unitPrice: '',
  serviceCatalogId: null,
  technicianId: null,
});

export const lineAmount = (line) => (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0);

export const extrasTotal = (extras) => extras.reduce((sum, line) => sum + lineAmount(line), 0);

export const extrasValid = (extras) =>
  extras.every((l) => l.description.trim() && Number(l.quantity) > 0 && l.unitPrice !== '' && Number(l.unitPrice) >= 0);

/**
 * Lines for the API (unitPrice in dollars). No add-ons and nobody specific on
 * the main service → [] (the job keeps a single price, as before).
 */
export const toApiLineItems = (mainPrice, extras, { mainTechnicianId = null } = {}) => {
  if (!extras.length && !mainTechnicianId) return [];
  return [
    { kind: 'MAIN', quantity: 1, unitPrice: Number(mainPrice) || 0, technicianId: mainTechnicianId || null },
    ...extras.map((l) => ({
      kind: 'EXTRA',
      description: l.description.trim(),
      quantity: Number(l.quantity),
      unitPrice: Number(l.unitPrice),
      serviceCatalogId: l.serviceCatalogId || null,
      technicianId: l.technicianId || null,
    })),
  ];
};

/**
 * Split API lines into { mainPrice, extras }. `inCents` is true for job
 * responses (amounts in cents), false for the last-job prefill (dollars).
 */
export const fromApiLineItems = (lineItems, { inCents = true } = {}) => {
  const toDollars = (v) => (inCents ? Number(v || 0) / 100 : Number(v || 0));
  const sorted = [...(lineItems || [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const main = sorted.find((l) => l.kind === 'MAIN');
  return {
    mainPrice: main ? toDollars(main.unitPrice) : null,
    mainTechnicianId: main?.technicianId || null,
    extras: sorted
      .filter((l) => l.kind !== 'MAIN')
      .map((l) => ({
        key: newKey(),
        description: l.description || '',
        quantity: Number(l.quantity) || 1,
        unitPrice: String(toDollars(l.unitPrice)),
        serviceCatalogId: l.serviceCatalogId || null,
        technicianId: l.technicianId || null,
      })),
  };
};

// Add-on suggestions from the service catalog (one per name)
let catalogCache = null;
const useExtraServices = () => {
  const [services, setServices] = useState(catalogCache || []);
  useEffect(() => {
    if (catalogCache) return;
    const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
    const params = new URLSearchParams({
      type: 'extra',
      tenantId: process.env.REACT_APP_TENANT_ID || '1',
      cityCode: process.env.REACT_APP_CITY_CODE || 'MCO',
    });
    fetch(`${apiBase}/api/service-catalog?${params}`)
      .then((res) => (res.ok ? res.json() : []))
      .then((rows) => {
        const byName = new Map();
        (Array.isArray(rows) ? rows : []).forEach((s) => {
          const existing = byName.get(s.name);
          if (!existing || (!(existing.rate > 0) && s.rate > 0)) byName.set(s.name, s);
        });
        catalogCache = [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
        setServices(catalogCache);
      })
      .catch((err) => logger.log('[JobLineItemsEditor] Catalog load failed:', err.message));
  }, []);
  return services;
};

const JobLineItemsEditor = ({ extras, onChange, technicians = [], mainPrice, main = null, disabled = false, embedded = false }) => {
  const catalog = useExtraServices();
  const techName = useMemo(
    () => Object.fromEntries(technicians.map((t) => [t.userId, t.name])),
    [technicians]
  );

  // A technician removed from the job no longer does their lines: back to the whole team
  useEffect(() => {
    if (extras.some((l) => l.technicianId && !techName[l.technicianId])) {
      onChange(extras.map((l) => (l.technicianId && !techName[l.technicianId] ? { ...l, technicianId: null } : l)));
    }
  }, [extras, techName, onChange]);
  const mainTechnicianId = main?.technicianId;
  const onMainTechnicianChange = main?.onTechnicianChange;
  useEffect(() => {
    if (mainTechnicianId && !techName[mainTechnicianId] && onMainTechnicianChange) onMainTechnicianChange(null);
  }, [mainTechnicianId, techName, onMainTechnicianChange]);

  const doneBySelect = (value, onSelect) => (
    <TextField
      select
      fullWidth
      size="small"
      label="Done by"
      disabled={disabled || technicians.length === 0}
      SelectProps={{ displayEmpty: true }}
      InputLabelProps={{ shrink: true }}
      value={techName[value] ? value : ''}
      onChange={(e) => onSelect(e.target.value || null)}
      helperText={technicians.length === 0 ? 'Assign technicians to choose one' : ' '}
    >
      <MenuItem value="">Whole team</MenuItem>
      {technicians.map((t) => (
        <MenuItem key={t.userId} value={t.userId}>{t.name}</MenuItem>
      ))}
    </TextField>
  );

  const update = (key, patch) => onChange(extras.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const remove = (key) => onChange(extras.filter((l) => l.key !== key));
  const add = () => onChange([...extras, emptyExtraLine()]);

  const total = (Number(mainPrice) || 0) + extrasTotal(extras);

  return (
    // `embedded`: inside a section that already has its own frame and title
    <Paper variant={embedded ? 'elevation' : 'outlined'} elevation={0} sx={{ p: embedded ? 0 : 2, bgcolor: embedded ? 'transparent' : undefined }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: extras.length || main ? 1.5 : 0 }}>
        <Box>
          {!embedded && <Typography variant="subtitle2">{main ? 'Services' : 'Add-on services'}</Typography>}
          {!extras.length && !main && (
            <Typography variant="caption" color="text.secondary">
              Inside the oven, a driveway power wash… each one priced and shown as its own invoice line.
            </Typography>
          )}
        </Box>
        <Button size="small" startIcon={<AddIcon />} onClick={add} disabled={disabled}>
          Add a service
        </Button>
      </Box>

      {main && (
        <Grid container spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              size="small"
              label="Main service"
              value={main.label || 'Cleaning'}
              InputProps={{ readOnly: true }}
              title="Change it with the Service field"
              helperText=" "
            />
          </Grid>
          <Grid item xs={4} sm={1.5}>
            <TextField fullWidth size="small" label="Qty" value={1} InputProps={{ readOnly: true }} helperText=" " />
          </Grid>
          <Grid item xs={8} sm={2}>
            {main.onPriceChange ? (
              <TextField
                fullWidth
                size="small"
                type="number"
                required
                label="Price ($)"
                disabled={disabled || main.priceDisabled}
                value={main.price}
                onChange={(e) => main.onPriceChange(e.target.value)}
                error={main.price !== '' && !(Number(main.price) >= 0)}
                helperText={main.priceHelperText || ' '}
                inputProps={{ min: 0, step: 5 }}
              />
            ) : (
              <TextField fullWidth size="small" label="Price ($)" value={main.price} InputProps={{ readOnly: true }} helperText="Price field above" />
            )}
          </Grid>
          <Grid item xs={10} sm={3}>
            {doneBySelect(main.technicianId, main.onTechnicianChange)}
          </Grid>
        </Grid>
      )}

      {extras.map((line) => {
        return (
          <Grid container spacing={1} alignItems="flex-start" key={line.key} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={5}>
              <Autocomplete
                freeSolo
                size="small"
                disabled={disabled}
                options={catalog}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                inputValue={line.description}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input') update(line.key, { description: value, serviceCatalogId: null });
                }}
                onChange={(_, value) => {
                  if (value && typeof value === 'object') {
                    update(line.key, {
                      description: value.name,
                      serviceCatalogId: value.id,
                      unitPrice: value.rate > 0 ? String(value.rate) : line.unitPrice,
                    });
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                      <span>{option.name}</span>
                      {option.rate > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {money(option.rate)}{option.unit && option.unit !== 'service' ? ` / ${option.unit}` : ''}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} required label="Service" placeholder="Pick or type, e.g. Driveway power wash" />
                )}
              />
            </Grid>
            <Grid item xs={4} sm={1.5}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Qty"
                disabled={disabled}
                value={line.quantity}
                onChange={(e) => update(line.key, { quantity: e.target.value })}
                error={!(Number(line.quantity) > 0)}
                inputProps={{ min: 1, step: 1 }}
              />
            </Grid>
            <Grid item xs={8} sm={2}>
              <TextField
                fullWidth
                size="small"
                type="number"
                required
                label="Price ($)"
                disabled={disabled}
                value={line.unitPrice}
                onChange={(e) => update(line.key, { unitPrice: e.target.value })}
                error={line.unitPrice !== '' && !(Number(line.unitPrice) >= 0)}
                helperText={Number(line.quantity) > 1 && line.unitPrice !== '' ? `= ${money(lineAmount(line))}` : ' '}
                inputProps={{ min: 0, step: 5 }}
              />
            </Grid>
            <Grid item xs={10} sm={3}>
              {doneBySelect(line.technicianId, (technicianId) => update(line.key, { technicianId }))}
            </Grid>
            <Grid item xs={2} sm={0.5} sx={{ display: 'flex', justifyContent: 'center' }}>
              <IconButton size="small" onClick={() => remove(line.key)} disabled={disabled} title="Remove this service">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        );
      })}

      {(extras.length > 0 || main) && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {extras.length > 0 && <>Main service {money(mainPrice)} + add-ons {money(extrasTotal(extras))} ={' '}</>}
          {!extras.length && 'Total: '}
          <strong>{money(total)}</strong>
        </Typography>
      )}
    </Paper>
  );
};

export default JobLineItemsEditor;
